import { BISHOP, EMPTY, KING, KNIGHT, isColor, typeOf, type Color } from './types.js';
import { SQUARE_COUNT, fileOf, rowOf } from './squares.js';

interface SideMaterial {
  /** Everything except the king. */
  readonly pieces: number;
  readonly knights: number;
  /** Square colour (0/1) of each bishop. */
  readonly bishopShades: readonly number[];
}

function sideMaterial(board: Int8Array, color: Color): SideMaterial {
  let pieces = 0;
  let knights = 0;
  const bishopShades: number[] = [];
  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    const code = board[sq] ?? EMPTY;
    if (code === EMPTY || !isColor(code, color) || typeOf(code) === KING) continue;
    pieces++;
    if (typeOf(code) === KNIGHT) knights++;
    if (typeOf(code) === BISHOP) bishopShades.push((fileOf(sq) + rowOf(sq)) % 2);
  }
  return { pieces, knights, bishopShades };
}

function isLoneMinor(m: SideMaterial): boolean {
  return m.pieces === 1 && (m.knights === 1 || m.bishopShades.length === 1);
}

/**
 * Whether `color` can never deliver mate: a lone king, or king and one minor
 * piece. Used when `color`'s opponent runs out of time — FIDE scores that a
 * draw. A conservative reading: helpmates with a lone minor against a loaded
 * board exist in theory but never decide a flag in practice.
 */
export function lacksMatingMaterial(board: Int8Array, color: Color): boolean {
  const m = sideMaterial(board, color);
  return m.pieces === 0 || isLoneMinor(m);
}

/**
 * Neither side can mate by any sequence: K v K, K+minor v K, and K+B v K+B
 * with both bishops on the same square colour.
 */
export function isInsufficientMaterial(board: Int8Array): boolean {
  const white = sideMaterial(board, 'w');
  const black = sideMaterial(board, 'b');
  if (white.pieces === 0 && black.pieces === 0) return true;
  if (white.pieces === 0 && isLoneMinor(black)) return true;
  if (black.pieces === 0 && isLoneMinor(white)) return true;
  const [whiteShade] = white.bishopShades;
  const [blackShade] = black.bishopShades;
  return (
    white.pieces === 1 && black.pieces === 1 &&
    whiteShade !== undefined && whiteShade === blackShade
  );
}
